/* istanbul ignore file */
import { Flex, TooltipStyled } from 'components/base/layout-components';
import _ from 'lodash';
import React, { useState } from 'react';
import sizeMe from 'react-sizeme';
import { PieChart, Pie, ResponsiveContainer, Cell, Tooltip } from 'recharts';

const COLORS = [
  '#00B81D',
  '#FF9800',
  '#5467FF',
  '#F61C3B',
  '#3B5998',
  '#BDBDBD'
];

const ExternalTooltip = ({
  externalTooltipPosition,
  onMouseEnter,
  onMouseLeave,
  size,
  content,
  item
}) => {
  const { height } = size;
  let styleProps = {};
  if (externalTooltipPosition === 'top') {
    styleProps = { left: item.tooltipPosition.x - 50, bottom: height + 10 };
  } else {
    styleProps = { left: item.tooltipPosition.x, top: height };
  }
  return (
    <TooltipStyled
      id='externalTooltip'
      padding='0.5rem'
      style={{
        ...styleProps,
        position: 'absolute',
        zIndex: 1
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {content(item)}
    </TooltipStyled>
  );
};

const tooltipContent = item => {
  const { name, value } = item;
  return (
    <div>
      <span>{`${name}: ${value}`}</span>
    </div>
  );
};

const WrappedPieChart = props => {
  const {
    size = {},
    data = [],
    dataKey,
    innerText,
    innerTextComponent,
    events,
    style,
    width,
    height,
    customTooltip,
    pieSize = {},
    showTooltip = true,
    noStroke,
    isGauge = false,
    paddingAngle,
    onCellClick,
    showExternalTooltip = false,
    externalTooltipPosition = 'bottom',
    externalTooltipContent
  } = props;
  const [externalTooltip, setExternalTooltip] = useState({});
  let tooltipTimeout;
  const handleShowTooltip = (item, i, e) => {
    clearTimeout(tooltipTimeout);
    setExternalTooltip({
      show: true,
      size: { width: pieWidth, height: pieHeight },
      item,
      content: externalTooltipContent || tooltipContent
    });
  };

  const handleHideTooltip = e => {
    tooltipTimeout = setTimeout(
      () => setExternalTooltip({ show: false, ...e }),
      1000
    );
  };

  const normalizedData = data.map(
    ({ name, value, total, color = null, ...rest }) => ({
      name,
      value,
      total,
      color,
      ...rest
    })
  );

  const pieWidth = _.get(pieSize, `width`, width);
  let pieHeight = _.get(pieSize, `height`, height);
  pieHeight = isGauge ? pieHeight / 2 : pieHeight;
  const pieLine = _.get(pieSize, `line`, 25);
  const pieRadius = (pieWidth - pieLine * 2) / 2;
  const pieInnerRadius =
    pieSize && pieSize.width ? pieRadius : size.width / 6.2;
  const pieOuterRadius =
    pieSize && pieSize.width ? pieRadius + pieLine : size.width / 6.2 + 30;
  const pieProps = {
    margin: { top: isGauge ? pieHeight : 0 }
  };

  return (
    <div style={{ width: 'unset', height: 'unset' }} position='relative'>
      {innerText && (
        <Flex
          width='100%'
          height='100%'
          position='absolute'
          items={isGauge ? 'end' : 'center'}
          justify='center'
          data-testid='inner-text-test-id'
        >
          {innerText}
        </Flex>
      )}
      {innerTextComponent && (
        <Flex
          width='100%'
          height='100%'
          position='absolute'
          items={isGauge ? 'end' : 'center'}
          justify='center'
          data-testid='inner-text-component-test-id'
        >
          {innerTextComponent()}
        </Flex>
      )}
      <div
        style={{
          width: `${pieWidth}px`,
          height: `${pieHeight}px`
        }}
      >
        <ResponsiveContainer width='100%' height='100%'>
          <PieChart style={style} {...pieProps} {...events}>
            <Pie
              isAnimationActive={false} // Bug TA-459 fix - https://github.com/recharts/recharts/issues/929
              data={normalizedData}
              innerRadius={pieInnerRadius}
              outerRadius={pieOuterRadius}
              dataKey={dataKey}
              {...(noStroke ? { stroke: 'none' } : {})}
              {...(paddingAngle ? { paddingAngle } : {})}
              {...(isGauge
                ? {
                    startAngle: 180,
                    endAngle: 0
                  }
                : {})}
              {...(showExternalTooltip
                ? {
                    onMouseEnter: handleShowTooltip,
                    onMouseLeave: handleHideTooltip
                  }
                : {})}
            >
              {normalizedData.map((entry, index) => {
                return (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color ?? COLORS[index % COLORS.length]}
                    {...(onCellClick
                      ? {
                          onClick: () => onCellClick(entry),
                          style: {
                            cursor: entry?.noCursor ? 'unset' : 'pointer'
                          }
                        }
                      : {})}
                  />
                );
              })}
            </Pie>
            {showTooltip && !showExternalTooltip && (
              <Tooltip
                isAnimationActive={false}
                {...(customTooltip ? { content: customTooltip } : {})}
              />
            )}
          </PieChart>
        </ResponsiveContainer>
        {showExternalTooltip && externalTooltip.show && (
          <ExternalTooltip
            data-testid='external-tooltip-test-id'
            onMouseEnter={() => {
              clearTimeout(tooltipTimeout);
            }}
            onMouseLeave={() => {
              setExternalTooltip({ show: false });
              clearTimeout(tooltipTimeout);
            }}
            externalTooltipPosition={externalTooltipPosition}
            {...externalTooltip}
          />
        )}
      </div>
    </div>
  );
};

export default sizeMe({ monitorHeight: true })(WrappedPieChart);
